import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  CalendarIcon, Phone, Mail, MapPin, MessageCircle, Loader2,
  Car, Flag, Users, Minus, Plus,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CONTACT, SERVICES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const times = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, "0");
  const m = i % 2 === 0 ? "00" : "30";
  return `${h}:${m}`;
});

const initial = {
  name: "", phone: "", email: "", service: "", pickup: "", dropoff: "",
  time: "", passengers: "1", message: "",
};

// Champs requis, dans l'ordre où on veut les valider / y ramener le focus.
const REQUIRED_FIELDS = ["name", "phone", "service", "pickup", "date", "time"];

const PHONE_RE = /^(0|\+33\s?)[1-9](\s?\d{2}){4}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form, date) {
  const errors = {};
  if (!form.name.trim()) errors.name = "Merci d'indiquer votre nom.";
  if (!form.phone.trim()) errors.phone = "Un numéro de téléphone est nécessaire.";
  else if (!PHONE_RE.test(form.phone.trim())) errors.phone = "Format de téléphone invalide (ex: 06 23 88 08 81).";
  if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) errors.email = "Adresse email invalide.";
  if (!form.service) errors.service = "Choisissez une prestation.";
  if (!form.pickup.trim()) errors.pickup = "Précisez le lieu de départ.";
  if (!date) errors.date = "Choisissez une date.";
  if (!form.time) errors.time = "Choisissez une heure.";
  return errors;
}

function computeProgress(form, date) {
  const checks = [!!form.name.trim(), !!form.phone.trim(), !!form.service, !!form.pickup.trim(), !!date, !!form.time];
  const done = checks.filter(Boolean).length;
  return { done, total: checks.length, pct: Math.round((done / checks.length) * 100) };
}

export default function Booking() {
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [sent, setSent] = useState(false);
  const [confirmedTrip, setConfirmedTrip] = useState(null);
  const fieldRefs = useRef({});

  // Pré-remplit la prestation quand on clique sur une carte "Prestations".
  useEffect(() => {
    const onPreselect = (e) => {
      setForm((f) => ({ ...f, service: e.detail }));
      setErrors((er) => ({ ...er, service: undefined }));
    };
    window.addEventListener("oka:preselect-service", onPreselect);
    return () => window.removeEventListener("oka:preselect-service", onPreselect);
  }, []);

  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (touched[k]) {
      setErrors((er) => validate({ ...form, [k]: v }, k === "date" ? v : date));
    }
  };

  const blur = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  // Détermine l'état visuel d'un champ : "error" (rouge), "valid" (vert) ou undefined (neutre).
  const status = (k, hasValue) => {
    if (!touched[k]) return undefined;
    if (errors[k]) return "error";
    if (hasValue) return "valid";
    return undefined;
  };

  const focusFirstError = (errs) => {
    const firstKey = REQUIRED_FIELDS.find((k) => errs[k]);
    if (firstKey && fieldRefs.current[firstKey]) {
      fieldRefs.current[firstKey].scrollIntoView({ behavior: "smooth", block: "center" });
      fieldRefs.current[firstKey].focus?.();
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form, date);
    setErrors(errs);
    setTouched(Object.fromEntries(Object.keys(initial).concat("date").map((k) => [k, true])));

    if (Object.keys(errs).length > 0) {
      toast.error("Merci de corriger les champs en rouge.");
      focusFirstError(errs);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        ...form,
        email: form.email || null,
        date: format(date, "dd/MM/yyyy"),
      });
      if (res.data.status === "success") {
        setConfirmedTrip({ form: { ...form }, date });
        setSent(true);
        setForm(initial);
        setDate(null);
        setTouched({});
        setErrors({});
      }
    } catch (err) {
      toast.error("Une erreur est survenue. Appelez-moi directement.");
    } finally {
      setLoading(false);
    }
  };

  const whatsapp = `https://wa.me/${CONTACT.phoneIntl.replace("+", "")}?text=${encodeURIComponent("Bonjour, je souhaite réserver un trajet avec OKA VTC.")}`;
  const progress = computeProgress(form, date);

  return (
    <section id="booking" data-testid="booking" className="bg-white py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* Left */}
          <div className="lg:col-span-5">
            <span className="text-xs tracking-[0.35em] uppercase text-[#1C2541]/50">Réservation</span>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight mt-5 text-[#0B132B]">
              Réservez<br /><span className="italic text-[#3a4a72]">votre trajet</span>
            </h2>
            <p className="text-[#1C2541]/70 leading-relaxed mt-6 max-w-md">
              Remplissez le formulaire et recevez une confirmation rapide, ou contactez-moi directement par téléphone ou WhatsApp.
            </p>

            <div className="mt-10 space-y-4">
              <a data-testid="contact-phone" href={`tel:${CONTACT.phoneIntl}`} className="group flex items-center gap-4 border border-[#0B132B]/10 p-4 hover:border-[#0B132B] transition-colors duration-300">
                <span className="w-11 h-11 bg-[#0B132B] text-white flex items-center justify-center"><Phone className="w-5 h-5" /></span>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-[#1C2541]/50">Téléphone</div>
                  <div className="font-semibold text-[#0B132B]">{CONTACT.phoneDisplay}</div>
                </div>
              </a>
              <a data-testid="contact-whatsapp" href={whatsapp} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4 border border-[#0B132B]/10 p-4 hover:border-[#0B132B] transition-colors duration-300">
                <span className="w-11 h-11 bg-[#0B132B] text-white flex items-center justify-center"><MessageCircle className="w-5 h-5" /></span>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-[#1C2541]/50">WhatsApp</div>
                  <div className="font-semibold text-[#0B132B]">Discuter maintenant</div>
                </div>
              </a>
              <div className="flex items-center gap-4 border border-[#0B132B]/10 p-4">
                <span className="w-11 h-11 bg-[#0B132B] text-white flex items-center justify-center"><Mail className="w-5 h-5" /></span>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-[#1C2541]/50">Email</div>
                  <div className="font-semibold text-[#0B132B]">{CONTACT.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-4 border border-[#0B132B]/10 p-4">
                <span className="w-11 h-11 bg-[#0B132B] text-white flex items-center justify-center"><MapPin className="w-5 h-5" /></span>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-[#1C2541]/50">Zone</div>
                  <div className="font-semibold text-[#0B132B]">{CONTACT.zone}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 relative bg-[#F8F9FA] border border-[#0B132B]/10 p-7 md:p-10 min-h-[560px]"
          >
            <AnimatePresence mode="wait">
              {sent ? (
                <motion.div
                  key="success"
                  data-testid="booking-success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="flex flex-col items-center text-center py-6"
                >
                  <svg width="76" height="76" viewBox="0 0 76 76" className="mb-6">
                    <motion.circle
                      cx="38" cy="38" r="35" fill="none" stroke="#0B132B" strokeWidth="2"
                      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                      transition={{ duration: 0.7, ease: "easeInOut" }}
                    />
                    <motion.path
                      d="M23 39 L33 49 L53 27" fill="none" stroke="#0B132B" strokeWidth="3.5"
                      strokeLinecap="round" strokeLinejoin="round"
                      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                      transition={{ duration: 0.45, delay: 0.55, ease: "easeOut" }}
                    />
                  </svg>
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.5 }}>
                    <h3 className="font-display text-2xl md:text-3xl text-[#0B132B]">Demande envoyée !</h3>
                    <p className="text-[#1C2541]/70 mt-3 max-w-sm mx-auto leading-relaxed">
                      Merci, votre demande a bien été transmise. Je vous recontacte très rapidement pour confirmer votre trajet.
                    </p>
                  </motion.div>

                  {confirmedTrip && (
                    <motion.div
                      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.9, duration: 0.5 }}
                      className="w-full max-w-sm mt-8"
                    >
                      <TripTicket form={confirmedTrip.form} date={confirmedTrip.date} compact />
                    </motion.div>
                  )}

                  <Button
                    type="button"
                    onClick={() => setSent(false)}
                    variant="outline"
                    className="mt-8 rounded-none h-12 px-8 border-[#0B132B]/20 hover:bg-[#0B132B] hover:text-white transition-colors duration-300"
                  >
                    Faire une nouvelle demande
                  </Button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="booking-form"
                  onSubmit={submit}
                  noValidate
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ProgressBar {...progress} />
                  <TripTicket form={form} date={date} />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Nom complet *" error={touched.name && errors.name}>
                      <Input
                        id="booking-name"
                        data-testid="input-name"
                        ref={(el) => (fieldRefs.current.name = el)}
                        className={fieldClass(status("name", !!form.name.trim()))}
                        value={form.name}
                        onChange={(e) => set("name")(e.target.value)}
                        onBlur={blur("name")}
                        placeholder="Votre nom"
                        aria-invalid={!!(touched.name && errors.name)}
                        aria-describedby={touched.name && errors.name ? "err-name" : undefined}
                      />
                    </Field>
                    <Field label="Téléphone *" error={touched.phone && errors.phone}>
                      <Input
                        id="booking-phone"
                        data-testid="input-phone"
                        ref={(el) => (fieldRefs.current.phone = el)}
                        type="tel"
                        className={fieldClass(status("phone", !!form.phone.trim()))}
                        value={form.phone}
                        onChange={(e) => set("phone")(e.target.value)}
                        onBlur={blur("phone")}
                        placeholder="06 XX XX XX XX"
                        aria-invalid={!!(touched.phone && errors.phone)}
                        aria-describedby={touched.phone && errors.phone ? "err-phone" : undefined}
                      />
                    </Field>
                    <Field label="Email" error={touched.email && errors.email}>
                      <Input
                        id="booking-email"
                        data-testid="input-email"
                        type="email"
                        className={fieldClass(status("email", !!form.email.trim()))}
                        value={form.email}
                        onChange={(e) => set("email")(e.target.value)}
                        onBlur={blur("email")}
                        placeholder="vous@email.com"
                        aria-invalid={!!(touched.email && errors.email)}
                        aria-describedby={touched.email && errors.email ? "err-email" : undefined}
                      />
                    </Field>
                    <Field label="Prestation *" error={touched.service && errors.service}>
                      <Select
                        value={form.service}
                        onValueChange={(v) => { set("service")(v); blur("service")(); }}
                      >
                        <SelectTrigger
                          data-testid="select-service"
                          ref={(el) => (fieldRefs.current.service = el)}
                          className={fieldClass(status("service", !!form.service))}
                        >
                          <SelectValue placeholder="Choisir" />
                        </SelectTrigger>
                        <SelectContent>
                          {SERVICES.map((s) => (
                            <SelectItem key={s.key} value={s.title} data-testid={`service-opt-${s.key}`}>{s.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Lieu de départ *" error={touched.pickup && errors.pickup}>
                      <Input
                        id="booking-pickup"
                        data-testid="input-pickup"
                        ref={(el) => (fieldRefs.current.pickup = el)}
                        className={fieldClass(status("pickup", !!form.pickup.trim()))}
                        value={form.pickup}
                        onChange={(e) => set("pickup")(e.target.value)}
                        onBlur={blur("pickup")}
                        placeholder="Adresse de départ"
                        aria-invalid={!!(touched.pickup && errors.pickup)}
                        aria-describedby={touched.pickup && errors.pickup ? "err-pickup" : undefined}
                      />
                    </Field>
                    <Field label="Destination">
                      <Input
                        id="booking-dropoff"
                        data-testid="input-dropoff"
                        className="rounded-none bg-white h-12"
                        value={form.dropoff}
                        onChange={(e) => set("dropoff")(e.target.value)}
                        placeholder="Adresse d'arrivée"
                      />
                    </Field>
                    <Field label="Date *" error={touched.date && errors.date}>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            data-testid="btn-date"
                            ref={(el) => (fieldRefs.current.date = el)}
                            type="button"
                            variant="outline"
                            onClick={() => setTouched((t) => ({ ...t, date: true }))}
                            className={`${fieldClass(status("date", !!date))} w-full justify-start font-normal`}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {date ? format(date, "dd MMMM yyyy", { locale: fr }) : <span className="text-muted-foreground">Choisir une date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 rounded-none" align="start">
                          <Calendar
                            mode="single"
                            selected={date}
                            onSelect={(d) => { setDate(d); setTouched((t) => ({ ...t, date: true })); setErrors((er) => validate(form, d)); }}
                            initialFocus
                            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          />
                        </PopoverContent>
                      </Popover>
                    </Field>
                    <Field label="Heure *" error={touched.time && errors.time}>
                      <Select value={form.time} onValueChange={(v) => { set("time")(v); blur("time")(); }}>
                        <SelectTrigger
                          data-testid="select-time"
                          ref={(el) => (fieldRefs.current.time = el)}
                          className={fieldClass(status("time", !!form.time))}
                        >
                          <SelectValue placeholder="Heure" />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          {times.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Passagers">
                      <PassengerStepper value={form.passengers} onChange={set("passengers")} />
                    </Field>
                  </div>

                  <div className="mt-5">
                    <Field label="Message (optionnel)">
                      <Textarea data-testid="input-message" className="rounded-none bg-white min-h-[90px]" value={form.message} onChange={(e) => set("message")(e.target.value)} placeholder="Bagages, vol, précisions..." />
                    </Field>
                  </div>

                  <Button
                    data-testid="submit-booking-btn"
                    type="submit"
                    disabled={loading}
                    className="mt-7 w-full rounded-none h-14 bg-[#0B132B] hover:bg-[#1C2541] text-white text-sm font-semibold tracking-wide transition-colors duration-300 disabled:opacity-70"
                  >
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours...</> : "Envoyer ma demande"}
                  </Button>
                  <p className="text-xs text-[#1C2541]/50 mt-4 text-center">Réponse rapide — devis gratuit et sans engagement.</p>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ProgressBar({ done, total, pct }) {
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#1C2541]/50">Votre demande</span>
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#1C2541]/50">{done}/{total}</span>
      </div>
      <div className="h-[3px] w-full bg-[#0B132B]/10 overflow-hidden">
        <motion.div
          className="h-full bg-[#0B132B]"
          initial={false}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    </div>
  );
}

function TripTicket({ form, date, compact }) {
  const displayDate = date ? format(date, "dd MMM yyyy", { locale: fr }) : null;
  return (
    <div
      data-testid="trip-ticket"
      className={`relative text-white overflow-hidden ${compact ? "mb-0" : "mb-8"}`}
      style={{ background: "radial-gradient(circle at 15% 0%, #1C2541, #0B132B 70%)" }}
    >
      <Car className="absolute -right-6 -bottom-6 w-32 h-32 text-white/[0.05] rotate-[-12deg] pointer-events-none" />

      <div className="relative z-10 px-6 md:px-8 pt-5 pb-4 flex items-center justify-between">
        <span className="text-[10px] tracking-[0.35em] uppercase text-white/50">
          {compact ? "Trajet confirmé" : "Bon de réservation"}
        </span>
        <span className="text-[10px] tracking-[0.35em] uppercase text-white/50">OKA VTC</span>
      </div>

      <div className="relative h-5 flex items-center px-6 md:px-8">
        <span className="absolute left-0 -translate-x-1/2 w-5 h-5 rounded-full" style={{ background: "#F8F9FA" }} />
        <div className="flex-1 mx-3 border-t border-dashed border-white/20" />
        <span className="absolute right-0 translate-x-1/2 w-5 h-5 rounded-full" style={{ background: "#F8F9FA" }} />
      </div>

      <div className="relative z-10 px-6 md:px-8 py-6">
        <div className="flex items-start gap-4">
          <div className="flex flex-col items-center pt-1.5">
            <span className="w-2 h-2 rounded-full bg-white shrink-0" />
            <span className="w-px flex-1 my-1 bg-white/25" style={{ minHeight: 26 }} />
            <Flag className="w-3 h-3 text-white/60 shrink-0" />
          </div>
          <div className="flex-1 space-y-5 min-w-0">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">Départ</div>
              <div className="font-display text-lg leading-snug mt-0.5 truncate">
                {form.pickup || <span className="text-white/30 italic font-sans text-sm">Lieu de départ</span>}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">Arrivée</div>
              <div className="font-display text-lg leading-snug mt-0.5 truncate">
                {form.dropoff || <span className="text-white/30 italic font-sans text-sm">Destination (optionnel)</span>}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-6 pt-5 border-t border-dashed border-white/15">
          <TicketDetail label="Prestation" value={form.service} />
          <TicketDetail label="Date" value={displayDate} />
          <TicketDetail label="Heure" value={form.time} />
        </div>
      </div>
    </div>
  );
}

function TicketDetail({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
      <div className="font-semibold text-sm mt-1 truncate">
        {value || <span className="text-white/30">—</span>}
      </div>
    </div>
  );
}

function PassengerStepper({ value, onChange }) {
  const n = parseInt(value, 10) || 1;
  const clamp = (v) => Math.min(4, Math.max(1, v));

  return (
    <div data-testid="input-passengers" className="flex items-center h-12 bg-white border border-input">
      <button
        type="button"
        onClick={() => onChange(String(clamp(n - 1)))}
        disabled={n <= 1}
        aria-label="Moins de passagers"
        className="w-12 h-full flex items-center justify-center text-[#0B132B] hover:bg-[#0B132B]/5 disabled:opacity-30 disabled:hover:bg-transparent transition-colors duration-200"
      >
        <Minus className="w-4 h-4" />
      </button>
      <div className="flex-1 flex items-center justify-center gap-2 text-[#0B132B]">
        <Users className="w-4 h-4 text-[#1C2541]/40" />
        <motion.span key={n} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }} className="font-semibold w-4 text-center">
          {n}
        </motion.span>
      </div>
      <button
        type="button"
        onClick={() => onChange(String(clamp(n + 1)))}
        disabled={n >= 4}
        aria-label="Plus de passagers"
        className="w-12 h-full flex items-center justify-center text-[#0B132B] hover:bg-[#0B132B]/5 disabled:opacity-30 disabled:hover:bg-transparent transition-colors duration-200"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}

function fieldClass(status) {
  // status: "error" | "valid" | undefined
  const base = "rounded-none bg-white h-12 transition-colors duration-200";
  if (status === "error") return `${base} border-red-400 focus-visible:ring-red-400`;
  if (status === "valid") return `${base} border-green-500 focus-visible:ring-green-500`;
  return base;
}

function Field({ label, error, children }) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="text-[11px] uppercase tracking-widest text-[#1C2541]/60">{label}</Label>
      {children}
      <AnimatePresence>
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -4, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="text-xs text-red-500"
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
